import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Alert, Box, Menu, MenuItem, Paper, Table, TableCell, TableHead, TableRow, Typography, useMediaQuery } from "@mui/material";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../app/hooks";
import { hasPermission, PermissionId } from "../../../../app/permissions";
import PrizaContainer from "../../../../features/base/grid/PrizaContainer";
import { isTokenReady } from "../../../../features/user/UserAvatar";
import { EnabledEnum, LoadState } from "../../../../redux/common";
import { minutesToTime, ScheduleInfo, timeToMinutes } from "../../../../redux/curriculum/model";
import { retrieveScheduleList, scheduleListSlice, selectScheduleList } from "../../../../redux/curriculum/scheduleListSlice";
import { retrieveSubjectList, selectSubjectList, subjectListSlice } from "../../../../redux/curriculum/subjectListSlice";
import { selectUser } from "../../../../redux/user/userSlice";
import { ScheduleInsUpd } from "../../../curriculum/ins-upd/schedule/ScheduleInsUpd";
import { CalendarEvent, layoutCalendarEvents } from "./CalendarEvent";
import { ScheduleDeleteModal } from "./ScheduleDeleteModal";
import { ScheduleItemCard } from "./ScheduleItemCard";
import { calculateStartEnd } from "./calculateStartEnd";
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { useNavigate } from 'react-router-dom';
import { routeClassroom } from '../../../../routes/classroom';
import { subjectPerformedListSlice } from '../../../../redux/curriculum/subjectPerformedListSlice';
import { subjectPerformedInsUpdSlice } from '../../../../redux/curriculum/subjectPerformedInsUpdSlice';
import dayjs from 'dayjs';
import { ClassroomInfo } from '../../../../redux/classroom/classroomSlice';

interface ContentListProps {
    schoolId: string;
    classroom: ClassroomInfo;
}

const ScheduleList = ({ schoolId, classroom }: ContentListProps) => {
    const dispatch = useAppDispatch();
    const user = useSelector(selectUser);
    const subjects = useSelector(selectSubjectList);
    const scheduleList = useSelector(selectScheduleList);
    const navigate = useNavigate();

    const classroomId = classroom.id;

    const canEdit = useMemo(
        () => hasPermission(user.priza.info, PermissionId.SCHOOL_ADMIN, schoolId)
            || hasPermission(user.priza.info, PermissionId.CLASSROOM_ADMIN, classroomId),
        [user, schoolId, classroomId]
    );

    const [changing, setChanging] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(undefined as ScheduleInfo | undefined);
    const [scheduleToDelete, setScheduleToDelete] = useState(undefined as ScheduleInfo | undefined);
    const [actionAnchorEl, setActionAnchorEl] = useState(undefined as HTMLElement | undefined);
    const [actionSchedule, setActionSchedule] = useState(undefined as ScheduleInfo | undefined);

    useEffect(() => {
        if (subjects.schoolId && subjects.schoolId != schoolId) {
            dispatch(subjectListSlice.actions.clean());
        }
        if (subjects.state.state == LoadState.NotStarted && isTokenReady(user))
            dispatch(retrieveSubjectList({ schoolId }));
    }, [subjects, schoolId, user]);
    useEffect(() => {
        dispatch(scheduleListSlice.actions.check({ schoolId, classroomId }));
        if (scheduleList.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveScheduleList({ schoolId, classroomId }));
        }
    }, [scheduleList, schoolId, classroomId, user]);

    const [startHour, endHour] = useMemo(() => {
        return calculateStartEnd(scheduleList);
    }, [scheduleList]);

    if (changing) {
        return <ScheduleInsUpd
            back={() => {
                setSelectedSchedule(undefined);
                setChanging(false);
            }}
            schoolId={schoolId}
            classroomId={classroomId}
            schedule={selectedSchedule} />;
    }

    const buttons = canEdit ? [
        {
            label: <AddIcon />,
            click: () => {
                setSelectedSchedule(undefined);
                setChanging(true);
            }
        }
    ] : undefined;

    const buildDayCell = (dayOfWeek: number) => {
        const items = [] as ReactNode[];
        for (let time = startHour; time < endHour; time += 60) {
            items.push(
                <Box key={time} sx={{ height: "60px", borderTop: "1px solid lightgray", verticalAlign: "top", alignItems: "center", justifyContent: "center" }}>
                </Box>
            );
        }
        const list = scheduleList.list?.filter((item) => item.dayOfWeek == dayOfWeek) || [];
        const calendarEvents = list.map((item) => ({
            ...item,
            start: timeToMinutes(item.startTime) || 0,
            end: timeToMinutes(item.endTime) || 0,
        } as CalendarEvent));
        const positionedEvents = layoutCalendarEvents(calendarEvents);

        for (let i = 0; i < positionedEvents.length; i++) {
            const schedule = positionedEvents[i];
            const subject = subjects.list?.find((s) => s.id == schedule.subjectId);
            const subjectName = subject ? subject.name : `Disciplina ${schedule.subjectId}`;
            items.push(
                <ScheduleItemCard
                    key={`${schedule.id}`}
                    schedule={schedule}
                    startHour={startHour}
                    subjectName={subjectName}
                    onMenuOpen={(event, sched) => {
                        setActionAnchorEl(event);
                        setActionSchedule(sched);
                    }}
                />
            );
        }
        return <Box sx={{ position: "relative", width: "100%", height: `${endHour - startHour}px` }}>
            {items}
        </Box>;
    };

    const buildContentHourCell = () => {
        const items = [] as ReactNode[];
        for (let time = startHour; time < endHour; time += 30) {
            items.push(
                <Box key={time} sx={{ height: "30px", borderTop: "1px solid lightgray", verticalAlign: "top", alignItems: "center", justifyContent: "center" }}>
                    <Typography sx={{ color: "black", fontSize: 10 }}>
                        {minutesToTime(time)}
                    </Typography>
                </Box>
            );
        }
        return <Box sx={{ position: "relative", width: "100%", height: `${endHour - startHour}px` }}>
            {items}
        </Box>;
    };

    const buildContent = () => {
        const sxHeader = {
            padding: "8px",
            borderLeft: "1px solid",
            width: "14%",
            color: (theme: any) => theme.palette.primary.contrastText,
            backgroundColor: (theme: any) => theme.palette.primary.main,
        };
        const sxBody = {
            padding: "1px",
            borderLeft: "1px solid",
            width: "14%",
            verticalAlign: "top",
        };
        return <Paper sx={{ p: 1, width: "100%" }}>
            {
                classroom.curriculum == EnabledEnum.NO &&
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Grade curricular desativada para esta sala de aula. A informação será mostrada aos pais apenas quando a grade curricular for ativada. Para configurar a grade curricular, edite as informações da sala de aula.
                </Alert>
            }
            <Table sx={{ minWidth: "800px" }}>
                <TableHead>
                    <TableRow>
                        <TableCell align="center" sx={{
                            ...sxHeader,
                            width: "2%",
                            borderLeft: undefined,
                        }}>Hora</TableCell>
                        <TableCell align="center" sx={sxHeader}>Domingo</TableCell>
                        <TableCell align="center" sx={sxHeader}>Segunda</TableCell>
                        <TableCell align="center" sx={sxHeader}>Terça</TableCell>
                        <TableCell align="center" sx={sxHeader}>Quarta</TableCell>
                        <TableCell align="center" sx={sxHeader}>Quinta</TableCell>
                        <TableCell align="center" sx={sxHeader}>Sexta</TableCell>
                        <TableCell align="center" sx={sxHeader}>Sábado</TableCell>
                    </TableRow>
                    <TableRow key={"1"} hover>
                        <TableCell
                            sx={{
                                ...sxBody,
                                textAlign: "center",
                                borderLeft: undefined,
                                width: "2%",
                            }}>
                            {buildContentHourCell()}
                        </TableCell>
                        <TableCell sx={{
                            ...sxBody,
                            borderLeft: undefined,
                        }}>
                            {buildDayCell(0)}
                        </TableCell>
                        <TableCell sx={sxBody}>{buildDayCell(1)}</TableCell>
                        <TableCell sx={sxBody}>{buildDayCell(2)}</TableCell>
                        <TableCell sx={sxBody}>{buildDayCell(3)}</TableCell>
                        <TableCell sx={sxBody}>{buildDayCell(4)}</TableCell>
                        <TableCell sx={sxBody}>{buildDayCell(5)}</TableCell>
                        <TableCell sx={sxBody}>{buildDayCell(6)}</TableCell>
                    </TableRow>
                </TableHead>
            </Table>
        </Paper>;
    };

    const schedulePerformed = (schedule: ScheduleInfo) => {
        const subject = subjects.list?.find((s) => s.id == schedule.subjectId);
        dispatch(subjectPerformedListSlice.actions.changing(true));
        const start = dayjs()
            .startOf("week")
            .day(schedule.dayOfWeek)
            .hour(Number(schedule.startTime.split(":")[0]))
            .minute(Number(schedule.startTime.split(":")[1]))
            .second(0)
            .millisecond(0);
        const end = dayjs()
            .startOf("week")
            .day(schedule.dayOfWeek)
            .hour(Number(schedule.endTime.split(":")[0]))
            .minute(Number(schedule.endTime.split(":")[1]))
            .second(0)
            .millisecond(0);
        dispatch(subjectPerformedInsUpdSlice.actions.set({
            id: -1,
            schoolId: Number(schoolId),
            classroomId: Number(classroomId),
            subjectId: Number(subject?.id),
            startedAt: start.toISOString(),
            endedAt: end.toISOString(),
            hoursPerformed: undefined,
        }));
        navigate(routeClassroom(schoolId, classroomId, "presence"));
    };

    return (
        <>
            <PrizaContainer
                toolbar={{
                    label: "Grade Curricular",
                    buttons: buttons
                }}
                state={scheduleList.state}
                retry={() => {
                    dispatch(subjectListSlice.actions.clean());
                    dispatch(scheduleListSlice.actions.clean());
                }}
                messages={{
                    empty: "Nenhuma disciplina associada a esta sala de aula.",
                    error: "Erro ao carregar disciplinas."
                }}
                content={buildContent}
            />
            <ScheduleDeleteModal
                schedule={scheduleToDelete}
                onClose={() => setScheduleToDelete(undefined)}
            />
            <Menu
                anchorEl={actionAnchorEl}
                open={actionAnchorEl !== undefined}
                onClose={() => {
                    setActionAnchorEl(undefined);
                    setActionSchedule(undefined);
                }}>
                <MenuItem
                    onClick={() => {
                        if (actionSchedule) {
                            setSelectedSchedule(actionSchedule);
                            setChanging(true);
                        }
                        setActionAnchorEl(undefined);
                        setActionSchedule(undefined);
                    }}>
                    <EditIcon sx={{ fontSize: 16, marginRight: "6px" }} />
                    Editar
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (actionSchedule) {
                            setScheduleToDelete(actionSchedule);
                        }
                        setActionAnchorEl(undefined);
                        setActionSchedule(undefined);
                    }}>
                    <DeleteIcon sx={{ fontSize: 16, marginRight: "6px", color: "error.main" }} />
                    Remover
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (actionSchedule) {
                            schedulePerformed(actionSchedule);
                        }
                        setActionAnchorEl(undefined);
                        setActionSchedule(undefined);
                    }}>
                    <FactCheckIcon sx={{ fontSize: 16, marginRight: "6px", color: "success.main" }} />
                    Criar Aula Ministrada
                </MenuItem>
            </Menu>
        </>
    );

};

export default ScheduleList;


